<template>
  <AppSection
    id="sms-credentials"
    :title="$getConst.pages.sms_configuration.sms_credentials_page_title"
    type="sub-section-1"
  >

    <b-overlay :show="isLoading">

      <template #overlay>
        <AppSpinner size="5" class="text-secondary"/>
      </template>

      <b-row>

        <b-col sm="6">
          <b-form-group  id="ovh-endpoint" :label="$t($getConst.pages.ovh_credentials.ovh_end_point_label) + ' *'" label-for="ovh-endpoint-input">
            <b-form-input
              id="ovh-endpoint-input"
              :disabled="!hasWritePermission"
              v-model.trim="$v.ovhDataToEdit.ovh_endpoint.$model"
              :class="{ 'is-invalid': $v.ovhDataToEdit.ovh_endpoint.$error }"
              :placeholder="$t($getConst.pages.ovh_credentials.ovh_end_point_placeholder) + ' *'"
              size="lg"
              required
            ></b-form-input>
            <AppFormFeedback  :errors="$v.ovhDataToEdit.ovh_endpoint">
            </AppFormFeedback>
          </b-form-group>
        </b-col>

        <b-col sm="6">
          <b-form-group  id="ovh-application-key" :label="$t($getConst.pages.ovh_credentials.ovh_application_key_label) + ' *'" label-for="ovh-application-key-input">
            <b-form-input
              id="ovh-application-key-input"
              :disabled="!hasWritePermission"
              v-model.trim="$v.ovhDataToEdit.ovh_applicationKey.$model"
              :class="{ 'is-invalid': $v.ovhDataToEdit.ovh_applicationKey.$error }"
              :placeholder="$t($getConst.pages.ovh_credentials.ovh_application_key_placeholder) + ' *'"
              size="lg"
              required
            ></b-form-input>
            <AppFormFeedback :errors="$v.ovhDataToEdit.ovh_applicationKey"/>
          </b-form-group>
        </b-col>

        <b-col sm="6">
          <b-form-group  id="ovh-consumer_key" :label="$t($getConst.pages.ovh_credentials.ovh_consumer_key_label) + ' *'" label-for="ovh-consumer_key-input">
            <b-form-input
              id="ovh-consumer_key-input"
              :disabled="!hasWritePermission"
              v-model.trim="$v.ovhDataToEdit.ovh_consumerKey.$model"
              :class="{ 'is-invalid': $v.ovhDataToEdit.ovh_consumerKey.$error }"
              :placeholder="$t($getConst.pages.ovh_credentials.ovh_consumer_key_placeholder) + ' *'"
              size="lg"
              required
            ></b-form-input>
            <AppFormFeedback :errors="$v.ovhDataToEdit.ovh_consumerKey"/>
          </b-form-group>
        </b-col>


        <b-col sm="6">
          <b-form-group id="ovh-application-secret" :label="$t($getConst.pages.ovh_credentials.ovh_application_secret_label) + ' *'" label-for="ovh-application-secret">
            <b-input-group size="lg">
              <template #prepend>
                <b-input-group-text :class="{'border-radius': passwordHidden}">
                  <a id="display-password-button" @click="onDisplayPassword" class="px-2">
                    <i :class="passwordHidden === true ? $getConst.icons.key : $getConst.icons.keyOpen " aria-hidden="true"></i>
                  </a>
                </b-input-group-text>
              </template>

              <template v-if="!passwordHidden">
                <b-form-input
                  id="ovh-application-secret-input"
                  :disabled="!hasWritePermission"
                  :type="passwordInputType"
                  v-model.trim="$v.ovhDataToEdit.ovh_applicationSecret.$model"
                  :class="{ 'is-invalid': $v.ovhDataToEdit.ovh_applicationSecret.$error }"
                  :placeholder="$t($getConst.pages.ovh_credentials.ovh_application_secret_placeholder) + ' *'"
                  size="lg"
                  required
                ></b-form-input>

                <b-input-group-append is-text id="toggle-password">
                  <a id="show-password-button" @click="onShowPassword">
                    <i :class="passwordInputType === 'password' ? $getConst.icons.eyeSlash : $getConst.icons.eye" aria-hidden="true"></i>
                  </a>
                </b-input-group-append>
              </template>

            </b-input-group>
            <AppFormFeedback :errors="$v.ovhDataToEdit.ovh_applicationSecret"/>
          </b-form-group>
        </b-col>

      </b-row>

       <b-row>
         <b-col cols="6" class="checkbox">
           <b-form-checkbox class="d-inline" switch v-model="$v.ovhDataToEdit.enable_sms.$model" size="lg">
             <label>{{$t($getConst.pages.scope.enable_sms)}}</label>
           </b-form-checkbox>
         </b-col>
       </b-row>

      <AppConfirmButtons
        v-if="hasWritePermission"
        :is-disabled="$v.ovhDataToEdit.$invalid"
        :is-loading="isLoading"
        @onSubmit="onSubmit"
        @onCancel="onCancel"
      />

    </b-overlay>
  </AppSection>
</template>

<script>
  import AppSection from "../../../../../components/common/sections/AppSection";
  import AppSpinner from "../../../../../components/common/utils/AppSpinner";
  import {required} from "vuelidate/lib/validators";
  import AppFormFeedback from "../../../../../components/common/form/AppFormFeedback";
  import AppConfirmButtons from "../../../../../components/common/utils/AppConfirmButtons";
  import {getSmsCredentialsByScope, updateSmsCredentials} from "../../../../../api/smsCredentials.api";
  export default {
    name: "index",
    components: {AppConfirmButtons, AppFormFeedback, AppSpinner, AppSection},
    props: {
      scope_id: null
    },
    computed: {
      hasWritePermission() {
        return this.hasPermission(this.$getConst.permissions.writeScopeSmsCredentials)
      }
    },
    data() {
      return {
        ovhData: null,
        isLoading: false,
        isTesting: false,
        ovhDataToEdit: {
          ovh_endpoint: null,
          ovh_applicationKey: null,
          ovh_applicationSecret: null,
          ovh_consumerKey: null,
          enable_sms: false
        },
        passwordInputType: "password",
        passwordHidden: true,
      }
    },
    validations: {
      ovhDataToEdit: {
        ovh_endpoint: {required},
        ovh_applicationKey: {required},
        ovh_applicationSecret: {},
        ovh_consumerKey: {required},
        enable_sms: {required}
      }
    },
    methods: {
      getSmsCredentials() {
        this.isLoading = true;
        getSmsCredentialsByScope(this.scope_id).then(
          (response) => {
            this.ovhData = Object.assign({}, response.data);
            this.ovhDataToEdit = response.data;
            this.isLoading = false;
          },
          (error) => {
            this.handleApiError(error);
            this.isLoading = false;
          }
        )
      },
      onSubmit() {
        this.isLoading = true;
        updateSmsCredentials(this.scope_id, this.ovhDataToEdit).then(
          (response) => {
            this.ovhData = Object.assign({}, response.data);
            this.isLoading = false;
            this.$snotify.success(this.$t(this.$getConst.pages.alerts.success_operation));
          },
          (error) => {
            this.handleApiError(error);
            this.isLoading = false;
          }
        )
      },
      onCancel() {
        this.ovhDataToEdit = Object.assign({}, this.ovhData);
      },
      onShowPassword() {
        if (this.passwordInputType === "password") {
          this.passwordInputType = "text";
        } else {
          this.passwordInputType = "password";
        }
      },
      onDisplayPassword() {
        this.passwordHidden = !this.passwordHidden;
        this.isActive = !this.isActive;
      },
    },
    created() {
      this.getSmsCredentials();
    }
  }
</script>

<style scoped>
  #ovh-application-secret-input {
    border-radius: 0 !important;
  }
  .border-radius {
    border-radius: 0.3rem !important;
  }
</style>
